/**
 * E12-F17-T15 — which projects are "fresh drafts": created by the "New Project"
 * button in this tab and not yet Saved from the card header.
 *
 * `create_manual` writes the row on click (legacy parity), so an abandoned create
 * (Cancel, or leaving the card without a Save) must discard it. This registry is
 * the client-side "who may be discarded" marker; the server re-checks that the row
 * is still an untouched draft before deleting (DELETE /projects/:id/draft), so a
 * stale marker can never delete real work.
 *
 * Backed by sessionStorage so a page reload of the fresh card keeps the marker
 * (falls back to memory when storage is unavailable). The deferred-discard helpers
 * survive React StrictMode's mount → unmount → mount: the unmount schedules the
 * discard on a macrotask and the immediate re-mount cancels it.
 */

const STORAGE_KEY = 'eer.freshDraftProjects';
const memory = new Set<number>();
const pending = new Map<number, ReturnType<typeof setTimeout>>();

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function read(): Set<number> {
  const s = storage();
  if (!s) return new Set(memory);
  try {
    const raw = JSON.parse(s.getItem(STORAGE_KEY) ?? '[]') as unknown;
    return new Set(Array.isArray(raw) ? raw.filter((v): v is number => Number.isInteger(v)) : []);
  } catch {
    return new Set();
  }
}

function write(ids: Set<number>): void {
  const s = storage();
  if (!s) {
    memory.clear();
    ids.forEach((id) => memory.add(id));
    return;
  }
  try {
    if (ids.size === 0) s.removeItem(STORAGE_KEY);
    else s.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* storage full / blocked — the server-side guard keeps this safe */
  }
}

/** Mark a project just created by "New Project" as a discardable draft. */
export function markFreshDraft(projectId: number): void {
  const ids = read();
  ids.add(projectId);
  write(ids);
}

/** Is this project a fresh (never Saved) "New Project" draft? */
export function isFreshDraft(projectId: number): boolean {
  return read().has(projectId);
}

/** Drop the marker (after a Save, or once the discard has been fired). */
export function forgetFreshDraft(projectId: number): void {
  const ids = read();
  if (ids.delete(projectId)) write(ids);
}

/**
 * Schedule `discard` for a fresh draft on a macrotask (the card unmounted). A
 * re-mount of the same project before it fires ({@link cancelScheduledDiscard})
 * cancels it — that is the StrictMode double-effect, not a real leave.
 */
export function scheduleDraftDiscard(projectId: number, discard: () => void): void {
  if (!isFreshDraft(projectId)) return;
  cancelScheduledDiscard(projectId);
  pending.set(
    projectId,
    setTimeout(() => {
      pending.delete(projectId);
      if (!isFreshDraft(projectId)) return;
      forgetFreshDraft(projectId);
      discard();
    }, 0),
  );
}

/** Cancel a pending discard (the card for this project mounted again). */
export function cancelScheduledDiscard(projectId: number): void {
  const t = pending.get(projectId);
  if (t !== undefined) {
    clearTimeout(t);
    pending.delete(projectId);
  }
}
